import { useState, useEffect } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Alert,
  AlertIcon,
  Box,
  Fade,
  InputGroup,
  InputRightElement,
  Checkbox,
  Flex,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import {
  fetchToken,
  PASSWORD_CHANGE_REQUIRED_MESSAGE,
} from "../apis/token-api";
import { resetWorkflowSession } from "../utils/workflowSession";
import { BeatLoader } from "react-spinners";
import { MdLock, MdVisibility, MdVisibilityOff } from "react-icons/md";
import { FaUser } from "react-icons/fa";

function LoginComponent() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const { username, setUsername } = useAppContext();
  const { password, setPassword } = useAppContext();
  const { globalToken, setGlobalToken } = useAppContext();
  const [rememberEmail, setRememberEmail] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  // Carregar email salvo do localStorage ao montar o componente
  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setUsername(savedEmail);
      setRememberEmail(true);
    }
  }, [setUsername]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!username.trim() || !password.trim()) {
      setError("Por favor, preencha todos os campos.");
      setIsErrorVisible(true);
      setIsLoading(false);
      return;
    }

    // Descarta sessão anterior (ex.: aberta pelo portal como PORTAL) antes de autenticar
    localStorage.removeItem("portal_token_payload");
    localStorage.removeItem("portal_refresh_token");
    sessionStorage.clear();
    resetWorkflowSession();

    try {
      const token = await fetchToken(username, password);
      setGlobalToken(token);

      if (token && token.access_token) {
        localStorage.setItem("portal_username", username);
        localStorage.setItem("portal_password", password);
        localStorage.setItem("portal_token_payload", JSON.stringify(token));
        if (token.refresh_token) {
          localStorage.setItem("portal_refresh_token", token.refresh_token);
        }
        // Salvar ou remover email do localStorage baseado no checkbox
        if (rememberEmail) {
          localStorage.setItem("rememberedEmail", username);
        } else {
          localStorage.removeItem("rememberedEmail");
        }

        setIsLoading(false);
        navigate("/home");
      } else {
        setError(
          "Credenciais inválidas. Por favor, verifique seu usuário e senha.",
        );
        setIsErrorVisible(true);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Erro ao fazer login:", error);
      setIsLoading(false);

      // Tratamento específico para diferentes tipos de erro
      let errorMessage =
        "Ocorreu um erro ao tentar fazer login. Por favor, tente novamente mais tarde.";

      if (
        error.isPasswordChangeRequired ||
        error.code === "PASSWORD_CHANGE_REQUIRED" ||
        error.message === "PASSWORD_CHANGE_REQUIRED"
      ) {
        errorMessage =
          error.userMessage || PASSWORD_CHANGE_REQUIRED_MESSAGE;
      } else if (error.status === 503) {
        errorMessage =
          "Serviço temporariamente indisponível. O sistema está sem licença ativa. Entre em contato com o suporte técnico.";
      } else if (error.status === 401) {
        errorMessage =
          "Credenciais inválidas. Por favor, verifique seu usuário e senha.";
      } else if (error.status === 403) {
        errorMessage =
          "Acesso negado. Você não tem permissão para acessar o sistema.";
      } else if (error.status === 404) {
        errorMessage =
          "Serviço de autenticação não encontrado. Entre em contato com o suporte técnico.";
      } else if (error.status === 500) {
        errorMessage =
          "Erro interno do servidor. Por favor, tente novamente mais tarde ou entre em contato com o suporte.";
      } else if (error.status === 0) {
        errorMessage =
          error.message ||
          "Erro de conexão. Verifique sua internet e tente novamente.";
      } else if (error.status >= 400 && error.status < 500) {
        errorMessage =
          error.message ||
          "Erro na requisição. Verifique suas credenciais e tente novamente.";
      } else if (error.status >= 500) {
        errorMessage =
          "Erro no servidor. Por favor, tente novamente mais tarde ou entre em contato com o suporte.";
      }

      setError(errorMessage);
      setIsErrorVisible(true);
    }
  };

  return (
    <Box as="form" onSubmit={handleLogin} w="100%">
      <Stack spacing={{ base: 3, sm: 4 }}>
        <FormControl id="email" isRequired>
          <FormLabel
            htmlFor="email"
            color="gray.700"
            fontWeight="600"
            fontSize={{ base: "xs", sm: "sm" }}
            mb={1.5}
            letterSpacing="0.3px"
          >
            Usuário
          </FormLabel>
          <InputGroup>
            <Box
              position="absolute"
              left="4"
              top="50%"
              transform="translateY(-50%)"
              zIndex={2}
              color="gray.400"
            >
              <FaUser size="16px" />
            </Box>
            <Input
              pl="12"
              focusBorderColor="#007abe"
              borderColor="gray.300"
              bg="white"
              borderWidth="2px"
              type="text"
              id="email"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                setIsErrorVisible(false);
              }}
              placeholder="Digite seu usuário"
              size="md"
              height={{ base: "38px", sm: "42px" }}
              _hover={{ borderColor: "gray.400" }}
              _focus={{
                borderColor: "#007abe",
                boxShadow: "0 0 0 3px rgba(0, 122, 190, 0.1)",
              }}
              _placeholder={{ color: "gray.400" }}
              borderRadius="lg"
              transition="all 0.2s"
            />
          </InputGroup>
        </FormControl>

        <FormControl id="password" isRequired>
          <FormLabel
            htmlFor="password"
            color="gray.700"
            fontWeight="600"
            fontSize={{ base: "xs", sm: "sm" }}
            mb={1.5}
            letterSpacing="0.3px"
          >
            Senha
          </FormLabel>
          <InputGroup>
            <Box
              position="absolute"
              left="4"
              top="50%"
              transform="translateY(-50%)"
              zIndex={2}
              color="gray.400"
            >
              <MdLock size="18px" />
            </Box>
            <Input
              pl="12"
              pr="12"
              focusBorderColor="#007abe"
              borderColor="gray.300"
              bg="white"
              borderWidth="2px"
              type={showPassword ? "text" : "password"}
              id="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setIsErrorVisible(false);
              }}
              placeholder="Digite sua senha"
              size="md"
              height={{ base: "38px", sm: "42px" }}
              _hover={{ borderColor: "gray.400" }}
              _focus={{
                borderColor: "#007abe",
                boxShadow: "0 0 0 3px rgba(0, 122, 190, 0.1)",
              }}
              _placeholder={{ color: "gray.400" }}
              borderRadius="lg"
              transition="all 0.2s"
            />
            <InputRightElement
              width="3.5rem"
              height={{ base: "38px", sm: "42px" }}
            >
              <Button
                size="sm"
                variant="ghost"
                h="100%"
                onClick={(e) => {
                  e.preventDefault();
                  setShowPassword(!showPassword);
                }}
                _hover={{ bg: "transparent", color: "#007abe" }}
                color="gray.500"
                transition="all 0.2s"
              >
                {showPassword ? (
                  <MdVisibilityOff size="20px" />
                ) : (
                  <MdVisibility size="20px" />
                )}
              </Button>
            </InputRightElement>
          </InputGroup>
        </FormControl>

        <Flex justify="flex-start" mt={{ base: -1, sm: -2 }}>
          <Checkbox
            colorScheme="blue"
            isChecked={rememberEmail}
            onChange={(e) => setRememberEmail(e.target.checked)}
            size={{ base: "xs", sm: "sm" }}
            color="gray.600"
            fontSize={{ base: "xs", sm: "sm" }}
          >
            Lembrar usuário
          </Checkbox>
        </Flex>

        <Button
          color="white"
          bg="#007abe"
          type="submit"
          mt={{ base: 2, sm: 3 }}
          size="md"
          height={{ base: "44px", sm: "52px" }}
          width="100%"
          isLoading={isLoading}
          loadingText="Entrando..."
          spinner={<BeatLoader size={8} color="white" />}
          _hover={{
            bg: "#0066a3",
            transform: "translateY(-1px)",
            boxShadow: "0 4px 12px rgba(0, 122, 190, 0.4)",
          }}
          _active={{
            transform: "translateY(0)",
            boxShadow: "0 2px 6px rgba(0, 122, 190, 0.3)",
          }}
          _disabled={{
            bg: "#9ca3af",
            cursor: "not-allowed",
            _hover: {
              bg: "#9ca3af",
              transform: "none",
            },
          }}
          transition="all 0.2s"
          fontWeight="600"
          fontSize="md"
          borderRadius="lg"
          boxShadow="0 2px 8px rgba(0, 122, 190, 0.3)"
          letterSpacing="0.3px"
        >
          {isLoading ? "Entrando..." : "Entrar"}
        </Button>
      </Stack>

      <Fade in={isErrorVisible}>
        <Alert
          status="error"
          mt={{ base: 3, sm: 4 }}
          borderRadius="lg"
          border="1px solid"
          borderColor="red.300"
          bg="red.50"
          py={{ base: 2, sm: 3 }}
        >
          <AlertIcon color="red.500" />
          <Box
            fontSize={{ base: "xs", sm: "sm" }}
            color="red.700"
            fontWeight="500"
            lineHeight="1.5"
          >
            {error}
          </Box>
        </Alert>
      </Fade>
    </Box>
  );
}

export default LoginComponent;
